import { useEffect, useState } from 'react';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import { FilterDataResponse } from '../../../Types/Analyse/FilterDataResponse';
import { useAppSelector } from '../../../Redux/Store/Store';
import { getClient } from '../../../Redux/Store/Slices/client';
import type { FilterConfig, ISelectOption } from './filterTypes';
import { sortOptionsByLabel } from './filterUtils';

const inFlight = new Map<string, Promise<FilterDataResponse>>();


const fetchFilterData = (endpoint: string): Promise<FilterDataResponse> => {
  const existing = inFlight.get(endpoint);
  if (existing) return existing;

  const promise = axiosInstance
    .get<FilterDataResponse>(endpoint)
    .then((res) => res.data)
    .catch((error) => {
      console.error(`Error fetching data for endpoint ${endpoint}:`, error);
      inFlight.delete(endpoint);
      return { organisations: [], departments: [], groups: [] } as FilterDataResponse;
    });

  inFlight.set(endpoint, promise);
  return promise;
};

export const useFilterOptions = (
  filters: FilterConfig[]
): Record<string, ISelectOption[]> => {
  const [filterData, setFilterData] = useState<Record<string, ISelectOption[]>>({});
  const client = useAppSelector(getClient);
  const clientId = client?.id;

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!clientId || clientId === 'undefined' || clientId === 'null') {
        return;
      }

      const endpoints = new Set<string>();
      for (const filter of filters) {
        if (filter.dataSource === 'api') {
          endpoints.add(filter.apiEndpoint || `GetAnalyseFilterData/${clientId}`);
        }
      }

      const responses = await Promise.all(
        Array.from(endpoints).map(async (endpoint) => [endpoint, await fetchFilterData(endpoint)] as const)
      );
      if (cancelled) return;

      const apiCache: Record<string, FilterDataResponse> = {};
      for (const [endpoint, data] of responses) {
        apiCache[endpoint] = data;
      }

      const newFilterData: Record<string, ISelectOption[]> = {};
      for (const filter of filters) {
        if (filter.dataSource === 'api') {
          const endpoint = filter.apiEndpoint || `GetAnalyseFilterData/${clientId}`;
          const data = apiCache[endpoint];
          // sortOptionsByLabel copies before sorting: the response objects are
          // cached in the module-level `inFlight` map, so an in-place sort would
          // permanently reorder the shared payload for every later consumer.
          if (filter.key === 'organisations') {
            newFilterData[filter.key] = sortOptionsByLabel(data.organisations);
          } else if (filter.key === 'departments') {
            newFilterData[filter.key] = sortOptionsByLabel(data.departments);
          } else if (filter.key === 'groups') {
            newFilterData[filter.key] = sortOptionsByLabel(data.groups);
          }
        } else if (filter.dataSource === 'props' && filter.data) {
          // Deliberately unsorted: props-sourced lists (Roles, Status, Impact)
          // carry a meaningful hierarchy/severity order. Ordering is the
          // caller's responsibility.
          newFilterData[filter.key] = filter.data;
        }
      }

      setFilterData(newFilterData);
    };

    fetchData().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [clientId, filters]);

  return filterData;
};
